import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FlaskConical,
  Plus,
  Pencil,
  Trash2,
  Share2,
  Users,
  LogOut,
  Eye,
  Wallet,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  createProject,
  deleteProject,
  leaveProject,
  roleOf,
  updateProject,
  watchProjects,
} from '../services/projects';
import type { Project } from '../lib/types';
import { formatDate } from '../lib/format';
import { Badge, Button, EmptyState, Input, Modal, Spinner, Textarea } from './ui';
import PoweredBy from './PoweredBy';
import ShareDialog from './ShareDialog';
import { LabAdminButton } from './LabAdmin';

export default function ProjectList() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Project | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Project | null>(null);
  const [confirmLeave, setConfirmLeave] = useState<Project | null>(null);
  const [sharing, setSharing] = useState<Project | null>(null);

  useEffect(() => {
    if (!user) return;
    return watchProjects(user.uid, setProjects, (e) => setError(e.message));
  }, [user]);

  const { mine, shared } = useMemo(() => {
    const all = projects ?? [];
    return {
      mine: all.filter((p) => p.ownerUid === user?.uid),
      shared: all.filter((p) => p.ownerUid !== user?.uid),
    };
  }, [projects, user?.uid]);

  if (!user) return null;

  const card = (p: Project, owned: boolean) => {
    const role = roleOf(p, user.uid);
    const memberCount = Object.keys(p.members ?? {}).length;
    return (
      <li key={p.id} className="card group relative p-4 transition-colors hover:border-ink-600">
        <Link to={`/p/${p.id}`} className="block">
          <h2 className="pr-16 font-medium">{p.name}</h2>
          {p.description && (
            <p className="mt-1 line-clamp-2 text-sm text-ink-400">{p.description}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-400">
            <span>
              {Array.isArray(p.readers)
                ? p.readers.length
                  ? p.readers.map((r) => r.name).join(' · ')
                  : 'No readers chosen yet'
                : `${p.formulationCount ?? 0} sample${(p.formulationCount ?? 0) === 1 ? '' : 's'}`}
            </span>
            {p.updatedAt && <span>· updated {formatDate(p.updatedAt)}</span>}
            {memberCount > 1 && (
              <Badge tone="info" className="ml-0.5">
                <Users size={10} />
                {memberCount}
              </Badge>
            )}
            {!owned && (
              <Badge tone="neutral">
                {role === 'viewer' ? <Eye size={10} /> : <Pencil size={10} />}
                {role === 'viewer' ? 'view only' : 'can edit'}
              </Badge>
            )}
          </div>
          {!owned && (
            <p className="mt-1.5 truncate text-xs text-ink-400">
              Owner: {p.members?.[p.ownerUid]?.displayName ?? p.members?.[p.ownerUid]?.email ?? 'unknown'}
            </p>
          )}
        </Link>

        {/* Always visible, never revealed on hover. A touch screen has no hover
            state at all, so these were effectively hidden on the device the app
            is mainly used on — and an action you cannot find is an action that
            does not exist. */}
        <div className="absolute right-3 top-3 flex gap-1">
          {owned ? (
            <>
              <button
                onClick={() => setSharing(p)}
                className="icon-btn text-ink-400 hover:bg-ink-800 hover:text-ink-100"
                aria-label={`Share ${p.name}`}
              >
                <Share2 size={14} />
              </button>
              <button
                onClick={() => setEditing(p)}
                className="icon-btn text-ink-400 hover:bg-ink-800 hover:text-ink-100"
                aria-label={`Rename ${p.name}`}
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={() => setConfirmDelete(p)}
                className="icon-btn text-ink-400 hover:bg-red-950 hover:text-red-300"
                aria-label={`Delete ${p.name}`}
              >
                <Trash2 size={14} />
              </button>
            </>
          ) : (
            <button
              onClick={() => setConfirmLeave(p)}
              className="icon-btn text-ink-400 hover:bg-red-950 hover:text-red-300"
              aria-label={`Leave ${p.name}`}
              title="Leave project"
            >
              <LogOut size={14} />
            </button>
          )}
        </div>
      </li>
    );
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Projects</h1>
          <p className="text-sm text-ink-400">Each project exports as one workbook.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/billing">
            <Button variant="ghost" icon={<Wallet size={16} />}>
              Credits
            </Button>
          </Link>
          <LabAdminButton />
          <Button variant="primary" icon={<Plus size={16} />} onClick={() => setEditing('new')}>
            New project
          </Button>
        </div>
      </div>

      {error && (
        <p className="mb-4 rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">
          {error}
        </p>
      )}

      {projects === null ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : projects.length === 0 ? (
        <EmptyState
          icon={<FlaskConical size={28} />}
          title="No projects yet"
          body="A project groups readings that belong in one workbook — usually one study."
          action={
            <Button variant="primary" icon={<Plus size={16} />} onClick={() => setEditing('new')}>
              Create the first one
            </Button>
          }
        />
      ) : (
        <div className="space-y-8">
          <section>
            <h2 className="field-label mb-2.5">Your projects</h2>
            {mine.length === 0 ? (
              <p className="rounded-lg border border-dashed border-ink-700 px-4 py-6 text-center text-sm text-ink-400">
                You have not created any projects yet.
              </p>
            ) : (
              <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {mine.map((p) => card(p, true))}
              </ul>
            )}
          </section>

          {shared.length > 0 && (
            <section>
              <h2 className="field-label mb-2.5">Shared with you</h2>
              <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {shared.map((p) => card(p, false))}
              </ul>
            </section>
          )}
        </div>
      )}

      <div className="mt-10 flex justify-center border-t border-ink-800 pt-5">
        <PoweredBy />
      </div>

      <ProjectDialog target={editing} onClose={() => setEditing(null)} />
      <DeleteDialog project={confirmDelete} onClose={() => setConfirmDelete(null)} />
      <LeaveDialog
        project={confirmLeave}
        uid={user.uid}
        onClose={() => setConfirmLeave(null)}
      />
      {sharing && (
        <ShareDialog
          open
          onClose={() => setSharing(null)}
          project={projects?.find((p) => p.id === sharing.id) ?? sharing}
          currentUid={user.uid}
        />
      )}
    </div>
  );
}

function ProjectDialog({
  target,
  onClose,
}: {
  target: Project | 'new' | null;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const isNew = target === 'new';
  const project = isNew ? null : target;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setName(project?.name ?? '');
    setDescription(project?.description ?? '');
  }, [target, project?.name, project?.description]);

  async function save() {
    if (!name.trim() || !user) return;
    setBusy(true);
    setError(null);
    try {
      if (isNew) await createProject(user, name, description);
      else if (project) await updateProject(project.id, { name, description });
      onClose();
    } catch (e) {
      console.error('project save failed', e);
      const code = (e as { code?: string })?.code;
      setError(
        code === 'permission-denied'
          ? 'Your organisation is read-only — its trial or subscription has ended.'
          : `Could not save the project: ${(e as Error)?.message ?? 'unknown error'}`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={target !== null} onClose={onClose} title={isNew ? 'New project' : 'Rename project'}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        className="space-y-4"
      >
        <div>
          <label className="field-label mb-1.5" htmlFor="project-name">
            Name
          </label>
          <Input
            id="project-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Project name"
          />
        </div>
        <div>
          <label className="field-label mb-1.5" htmlFor="project-desc">
            Description
          </label>
          <Textarea
            id="project-desc"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
          />
        </div>
        {error && (
          <p className="rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">{error}</p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!name.trim()}>
            {isNew ? 'Create' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function DeleteDialog({ project, onClose }: { project: Project | null; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState('');

  useEffect(() => setTyped(''), [project]);

  async function remove() {
    if (!project) return;
    setBusy(true);
    try {
      await deleteProject(project.id);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  const memberCount = Object.keys(project?.members ?? {}).length;

  return (
    <Modal open={!!project} onClose={onClose} title="Delete project">
      <p className="text-sm text-ink-300">
        This deletes <strong className="text-ink-100">{project?.name}</strong>, everything recorded
        in it, and the whole capture history. It cannot be undone.
      </p>
      {memberCount > 1 && (
        <p className="mt-2 rounded-lg border border-amber-800 bg-amber-950 p-2.5 text-sm text-amber-200">
          {memberCount - 1} other {memberCount === 2 ? 'person' : 'people'} can see this project.
          They will lose it too.
        </p>
      )}
      <p className="mt-3 text-sm text-ink-400">
        Export first if you want a copy. Type the project name to confirm.
      </p>
      <Input
        className="mt-3"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={project?.name}
        aria-label="Type the project name to confirm"
      />
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          loading={busy}
          disabled={typed.trim() !== project?.name.trim()}
          onClick={() => void remove()}
        >
          Delete permanently
        </Button>
      </div>
    </Modal>
  );
}

function LeaveDialog({
  project,
  uid,
  onClose,
}: {
  project: Project | null;
  uid: string;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);

  return (
    <Modal open={!!project} onClose={onClose} title="Leave project">
      <p className="text-sm text-ink-300">
        Remove yourself from <strong className="text-ink-100">{project?.name}</strong>? Nothing is
        deleted — the owner keeps the project and can add you back.
      </p>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          loading={busy}
          onClick={async () => {
            if (!project) return;
            setBusy(true);
            try {
              await leaveProject(project.id, uid);
              onClose();
            } finally {
              setBusy(false);
            }
          }}
        >
          Leave
        </Button>
      </div>
    </Modal>
  );
}
